/**
 * unloadSave — fire a save that SURVIVES a full document unload.
 *
 * IDI-336 (ARM 2): closing the tab, Back, the address bar or an external link
 * with an edit still inside the autosave debounce lost the edit, and NO
 * request was issued at all. The beforeunload handler did call performSave,
 * but that goes through axios, whose request interceptor first awaits
 * `fetchAuthSession()`. The document is torn down before that promise
 * resolves, so the PUT never leaves the browser.
 *
 * Fix: a SYNCHRONOUS path. Read the Cognito id token straight from Amplify's
 * localStorage (no await), and send `fetch(url, { keepalive: true })` in the
 * same tick. keepalive requests are allowed to outlive the page (Fetch spec),
 * and unlike navigator.sendBeacon they can carry the Authorization header and
 * use PUT. The one limit is a 64 KB body; above that we skip (and the caller
 * still has the native "unsaved changes" prompt as a backstop).
 */

export const KEEPALIVE_MAX_BYTES = 64 * 1024;

/** Synchronously read the signed-in Cognito id token Amplify v6 persists. */
export function readIdTokenSync(storage = (typeof window !== 'undefined' ? window.localStorage : null)) {
  if (!storage) return null;
  try {
    const clientId = process.env.REACT_APP_COGNITO_CLIENT_ID
      || process.env.REACT_APP_COGNITO_USER_POOL_CLIENT_ID;
    const prefix = 'CognitoIdentityServiceProvider.';
    const keys = [];
    for (let i = 0; i < storage.length; i += 1) keys.push(storage.key(i));
    const lastUserKey = keys.find((k) => k && k.startsWith(prefix) && k.endsWith('.LastAuthUser')
      && (!clientId || k === `${prefix}${clientId}.LastAuthUser`));
    if (!lastUserKey) return null;
    const base = lastUserKey.slice(0, -'.LastAuthUser'.length);
    const user = storage.getItem(lastUserKey);
    return (user && storage.getItem(`${base}.${user}.idToken`)) || null;
  } catch {
    return null;
  }
}

function tenantFromToken(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return payload['custom:tenant_id'] || null;
  } catch {
    return null;
  }
}

/**
 * Send `body` as a keepalive PUT to `${API_BASE}${path}`. Returns a string
 * describing what happened (for tests and a console breadcrumb); never throws.
 */
export function sendUnloadSave(path, body, {
  apiBase = process.env.REACT_APP_API_URL,
  fetchImpl = (typeof fetch !== 'undefined' ? fetch : null),
  storage,
} = {}) {
  try {
    if (!fetchImpl || !apiBase || !path) return 'unavailable';
    const token = readIdTokenSync(storage);
    if (!token) return 'no-session';
    const json = JSON.stringify(body);
    // Byte length, not string length (multi-byte chars).
    const bytes = typeof TextEncoder !== 'undefined' ? new TextEncoder().encode(json).length : json.length;
    if (bytes > KEEPALIVE_MAX_BYTES) return 'too-large';
    const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
    const tenant = tenantFromToken(token);
    if (tenant) headers['X-Tenant-Id'] = tenant;
    fetchImpl(`${apiBase}${path}`, {
      method: 'PUT',
      headers,
      body: json,
      keepalive: true,
      mode: 'cors',
    }).catch(() => {});
    return 'sent';
  } catch {
    return 'error';
  }
}
